import * as React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel
} from '@mui/material';
import {UserActivityTypes} from "../../types";
import {When} from "react-if";
import Loading from "../loading";

interface Props{
    loading?: boolean;
    data: UserActivityTypes.TeamUserActivityData[]
    onSort?: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
    currentSortBy?: string;
    currentSortOrder?: 'ASC' | 'DESC';
}

type SortableColumn = 'username' | 'teamName' | 'sosAlertCount' | 'fakeCallCount' | 'checkOnMeCount' | 'fieldSafetyReportCount' | 'riskReviewCount' | 'daysActiveCount' | 'percentDaysActive';

interface ColumnConfig {
    key: SortableColumn;
    label: string;
    align: 'left' | 'right' | 'inherit';
    sortable: boolean;
}

const columns: ColumnConfig[] = [
    { key: 'username', label: 'User Name', align: 'inherit', sortable: true },
    { key: 'teamName', label: 'Team Name', align: 'inherit', sortable: true },
    { key: 'sosAlertCount', label: 'SOS Alerts', align: 'right', sortable: true },
    { key: 'fakeCallCount', label: 'Fake Call', align: 'right', sortable: true },
    { key: 'checkOnMeCount', label: 'Check On Me', align: 'right', sortable: true },
    { key: 'fieldSafetyReportCount', label: 'Field Safety Reports', align: 'right', sortable: true },
    { key: 'riskReviewCount', label: 'Risk Reviews', align: 'right', sortable: true },
    { key: 'daysActiveCount', label: 'Days Active', align: 'right', sortable: true },
    { key: 'percentDaysActive', label: '% of Days Active', align: 'right', sortable: true }
];

export const TeamUserActivityTable: React.FC<Props> = ({
                                                           data,
                                                           loading,
                                                           onSort,
                                                           currentSortBy = 'username',
                                                           currentSortOrder = 'ASC'
                                                       }) => {

    const handleSort = (column: SortableColumn) => {
        if (!onSort) return;

        let newSortOrder: 'ASC' | 'DESC' = 'ASC';

        if (currentSortBy === column) {
            // Toggle sort order if clicking the same column
            newSortOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
            // Set new column and default to ascending
            newSortOrder = 'ASC';
        }

        onSort(column, newSortOrder);
    };

    return (
        <div style={{ width: '100%', height: 400, overflow: 'auto' }}>

            <TableContainer sx={{ borderRadius: 0, maxHeight: 400, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                <When
                    condition={
                        !loading && (!data || data.length === 0)
                    }
                >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', margin: 'auto' }}>
                        <Typography align={"center"} fontSize={14} fontWeight={600}>
                            No data available
                        </Typography>
                    </Box>
                </When>
                <When condition={ data && Object.keys(data).length > 0}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow sx={{ borderBottom: '4px solid white' }} >
                                {columns.map((column) => (
                                    <TableCell
                                        sx={{
                                            backgroundColor: '#BDBABA !important',
                                            backgroundImage: 'none',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            padding: '8px 16px'
                                        }}
                                        key={column.key}
                                        align={column.align}
                                    >
                                        {column.sortable && onSort ? (
                                            <TableSortLabel
                                                active={currentSortBy === column.key}
                                                direction={currentSortBy === column.key ? currentSortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
                                                onClick={!loading ? () => handleSort(column.key) : undefined}
                                                sx={{
                                                    '& .MuiTableSortLabel-root': {
                                                        color: '#000000',
                                                        fontWeight: 700,
                                                        fontSize: 14
                                                    },
                                                    '& .MuiTableSortLabel-root:hover': {
                                                        color: '#333333'
                                                    },
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: '#000000 !important'
                                                    }
                                                }}
                                            >
                                                <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                    {column.label}
                                                </Typography>
                                            </TableSortLabel>
                                        ) : (
                                            <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                {column.label}
                                            </Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading}>
                            <TableBody>
                                {data.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            borderBottom: '4px solid white',
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#F5F4F4'
                                            },
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#DDF5FF'
                                            },
                                            ":hover": {
                                                backgroundColor: '#dbd8d8',
                                                cursor: 'pointer'
                                            },
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row.username}
                                        </TableCell>
                                        <TableCell align="left">&nbsp;{row.teamName}</TableCell>
                                        <TableCell align="right">{row.sosAlertCount}</TableCell>
                                        <TableCell align="right">{row.fakeCallCount}</TableCell>
                                        <TableCell align="right">{row.checkOnMeCount}</TableCell>
                                        <TableCell align="right">{row.fieldSafetyReportCount}</TableCell>
                                        <TableCell align="right">{row.riskReviewCount}</TableCell>
                                        <TableCell align="right">{row.daysActiveCount}</TableCell>
                                        <TableCell align="right">{Math.round(Number(row.percentDaysActive))}&#37;</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>
                </When>
                <When condition={loading}>
                    <Loading />
                </When>
            </TableContainer>
        </div>
    );
}